import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { FinancialProvider } from './FinancialContext';
import { toast } from '@/hooks/ui/use-toast';
import type { Business } from '@/types/index';

interface BusinessContextType {
  businesses: Business[];
  selectedBusinessId: string | null;
  selectedBusiness: Business | null;
  loadingBusinesses: boolean;
  switchBusiness: (businessId: string) => void;
  setBusinesses: Dispatch<SetStateAction<Business[]>>;
  updateBusinessInList: (businessId: string, updates: Partial<Business>) => void;
  refreshBusinesses: () => Promise<void>;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export const useBusinessContext = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusinessContext must be used within BusinessProvider');
  }
  return context;
};

export function BusinessProvider({ children }: { children: ReactNode }) {
  const { user, session, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);

  const selectedBusiness = useMemo(
    () => businesses.find(b => b.id === selectedBusinessId) ?? null,
    [businesses, selectedBusinessId]
  );

  // Switch business - data updates automatically via businessId in query keys
  const switchBusiness = useCallback((newBusinessId: string) => {
    if (newBusinessId === selectedBusinessId) return;

    // Update state
    setSelectedBusinessId(newBusinessId);

    // Persist to localStorage
    localStorage.setItem('selectedBusinessId', newBusinessId);

    // Show confirmation toast
    const businessName = businesses.find(b => b.id === newBusinessId)?.name;
    if (businessName) {
      toast(`Switched to: ${businessName}`);
    }
  }, [selectedBusinessId, businesses]);

  // Fetch businesses function
  const fetchBusinesses = useCallback(async (userId: string) => {
    setLoadingBusinesses(true);

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !sessionData?.session) {
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError || !refreshData?.session) {
          throw new Error("Authentication session expired. Please sign in again.");
        }
      }

      const { data: businessesData, error: businessesError } = await supabase
        .from("businesses")
        .select(`
          id,
          name,
          favicon_url,
          country_code,
          created_at,
          address,
          timezone,
          latitude,
          longitude,
          dotypos_enabled,
          dotypos_cloud_id,
          last_attendance_sync_at,
          payment_processing_enabled,
          financial_enabled,
          enabled_features,
          auto_close_on_public_holidays,
          business_opening_hours (
            id,
            day_of_week,
            opening_time,
            closing_time,
            is_closed_all_day,
            is_24_hours,
            is_open
          )
        `)
        .eq("user_id", userId);

      if (businessesError) {
        if (businessesError.code === 'PGRST301' || businessesError.message?.includes('JWT')) {
          throw new Error("Authentication session expired. Please sign in again.");
        }
        throw businessesError;
      }

      // Transform the data to include opening_hours field
      const fetchedBusinesses = (businessesData || []).map(business => ({
        ...business,
        opening_hours: business.business_opening_hours?.map(oh => ({
          ...oh,
          business_id: business.id
        })) || null,
      })) as Business[];

      setBusinesses(fetchedBusinesses);

      // Determine the initial selected business ID with cache validation
      const storedBusinessId = localStorage.getItem('selectedBusinessId');
      const isValidStoredId = storedBusinessId && fetchedBusinesses.some(b => b.id === storedBusinessId);
      let initialSelectedId: string | null = null;

      if (isValidStoredId) {
        initialSelectedId = storedBusinessId;
      } else if (fetchedBusinesses.length > 0) {
        initialSelectedId = fetchedBusinesses[0].id;
        localStorage.setItem('selectedBusinessId', initialSelectedId);
      } else {
        localStorage.removeItem('selectedBusinessId');
      }
      setSelectedBusinessId(initialSelectedId);

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      if (errorMessage.includes("Authentication session expired")) {
        toast.error("Session expired", {
          description: "Please sign in again to continue.",
        });
        localStorage.removeItem('selectedBusinessId');
        setBusinesses([]);
        setSelectedBusinessId(null);
        navigate("/auth");
      } else {
        toast.error("Failed to load your businesses", {
          description: errorMessage.includes("JWT") || errorMessage.includes("auth")
            ? "Please try refreshing the page or signing in again."
            : "An unexpected error occurred."
        });
        setBusinesses([]);
        setSelectedBusinessId(null);
        localStorage.removeItem('selectedBusinessId');
      }
    } finally {
      setLoadingBusinesses(false);
    }
  }, [navigate]);

  // Refresh businesses
  const refreshBusinesses = useCallback(async () => {
    if (user?.id) {
      await fetchBusinesses(user.id);
    }
  }, [user?.id, fetchBusinesses]);

  // Update a specific business's details in the local list
  const updateBusinessInList = useCallback((businessId: string, updatedDetails: Partial<Business>) => {
    setBusinesses(prevBusinesses =>
      prevBusinesses.map(business =>
        business.id === businessId
          ? { ...business, ...updatedDetails }
          : business
      )
    );
  }, []);

  // Track if we've already fetched for this user to prevent duplicate fetches
  // when session object reference changes but user is the same
  const fetchedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (authLoading) return;

    if (!session || !user?.id) {
      setLoadingBusinesses(false);
      fetchedForUserRef.current = null;
      return;
    }

    // Skip if we've already fetched for this user (prevents refetch on tab focus)
    if (fetchedForUserRef.current === user.id) {
      return;
    }

    fetchedForUserRef.current = user.id;
    const loadingTimeout = setTimeout(() => setLoadingBusinesses(false), 10000);

    fetchBusinesses(user.id).finally(() => clearTimeout(loadingTimeout));

    return () => clearTimeout(loadingTimeout);
  }, [user?.id, authLoading, fetchBusinesses]); // Removed session from deps - only care about user.id

  const contextValue = useMemo(() => ({
    businesses,
    selectedBusinessId,
    selectedBusiness,
    loadingBusinesses,
    switchBusiness,
    setBusinesses,
    updateBusinessInList,
    refreshBusinesses,
  }), [businesses, selectedBusinessId, selectedBusiness, loadingBusinesses, switchBusiness, updateBusinessInList, refreshBusinesses]);

  return (
    <BusinessContext.Provider value={contextValue}>
      <FinancialProvider>
        {children}
      </FinancialProvider>
    </BusinessContext.Provider>
  );
}
